import clsx from "clsx";

import { useUser } from "@components/user-context";
import { useFeatureFlags } from "@hooks/http/use-feature-flag";

import { Platform } from "../constants";

import { SetupSection } from "./components/setup-section";
import {
  Code,
  ListIndent,
  OrderedList,
  SubSubSectionHeader,
} from "./components/styled";
import { SubmitTicketButton } from "./components/submit-ticket-button";
import { URLVerification } from "./components/utms";
import { VideoContent } from "./components/video-content";
import { type PlatformDetails } from "./platform-details";
import { UtmSpendAdditionalDetails } from "./utm-spend-details";

const PLATFORM = Platform.TRADE_DESK;

export const TradeDeskDetails: PlatformDetails = {
  northbeamDocUrl: "https://docs.northbeam.io/docs/tracking-for-tradedesk",
  officialDocUrl: "",
  additionalDetails: UtmSpendAdditionalDetails(PLATFORM),
};

const URL_UTMS =
  "https://<COLLECTOR_SUBDOMAIN>.<CLIENT_WEBSITE>/i?aid=<CLIENT_ID>&e=se&se_ca=northbeam&se_ac=impression&se_la=tradedesk&se_pr=%%TTD_CAMPAIGNID%%%7C%3A%7C%%TTD_ADGROUPID%%%7C%3A%7C%%TTD_CREATIVEID%%%7C%3A%7C%%TTD_DEVICEOSFAMILY%%%7C%3A%7C%%TTD_DEVICETYPE%%%7C%3A%7C%%TTD_SITE%%%7C%3A%7C%%TTD_SUPPLYVENDOR_INT%%";

export const TradeDeskSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { user } = useUser();

  const displayTradeDeskConnectionCard = hasFeatureFlag(
    "enableTradeDeskConnection",
  );
  if (!displayTradeDeskConnectionCard) {
    return <TradeDeskManualSetup />;
  }
  return (
    <>
      <SetupSection title="Define the list of channels you want to connect to Northbeam">
        <span className="text-sm leading-tight">
          Some channels have limited attributions in Northbeam. <br />
          For that reason, define the list of channels you intend to connect to
          validate their current attribution.
        </span>
      </SetupSection>
      <SetupSection title="Insert click tracking UTMs">
        <p>
          In order for us to fetch impressions, we need a specific Impression
          Tracking URL to be placed. Every customer has a unique URL -- based on
          the information above.
        </p>
        <VideoContent
          platform={PLATFORM}
          title="Learn how to structure TradeDesk UTMs"
          videoUrl="https://www.loom.com/embed/1577c9d4a61e45519b87b21ad7d202f4?sid=5880cbf7-ab25-4a82-b7f4-6b625b391338"
        />
        <div className="space-y-2">
          <SubSubSectionHeader>
            Make sure you’ve followed these steps:
          </SubSubSectionHeader>
          <OrderedList variant="upper-alpha">
            <>Log into Trade desk</>
            <>
              Update ‘Click Through’ URLs with the following UTM
              <Code block className={clsx("mt-2", ListIndent())}>
                {/* eslint-disable max-len */}
                nb_platform=tradedesk&nb_campaignid=%%TTD_CAMPAIGNID%%&nb_adsetid=%%TTD_ADGROUPID%%&nb_creativeid=%%TTD_CREATIVEID%%&nb_devicefamily=%%TTD_DEVICEOSFAMILY%%&nb_devicetype=%%TTD_DEVICETYPE%%&nbt_site=%%TTD_SITE%%&nbt_vendor=%%TTD_SUPPLYVENDOR_INT%%
              </Code>
            </>
            <>
              An example URL with the Click UTM.
              <div className={clsx("mt-2 space-y-2", ListIndent())}>
                <div>
                  <i>Example, do not copy: </i>
                </div>
                <Code block>
                  https://northbeam.io?nb_platform=tradedesk&nb_campaignid=%%TTD_CAMPAIGNID%%&nb_adsetid=%%TTD_ADGROUPID%%&nb_creativeid=%%TTD_CREATIVEID%%&nb_devicefamily=%%TTD_DEVICEOSFAMILY%%&nb_devicetype=%%TTD_DEVICETYPE%%&nbt_site=%%TTD_SITE%%&nbt_vendor=%%TTD_SUPPLYVENDOR_INT%%
                </Code>
              </div>
            </>
          </OrderedList>
        </div>
        <div>
          <div className="flex gap-2">
            <b>Please note:</b>
            <span>It’s important to ensure your UTM begins with</span>
            <Code>&</Code>
            <span>or</span>
            <Code>?</Code>
          </div>
          <OrderedList variant="disc">
            <>
              If you are using existing parameters, please use <Code>&</Code>{" "}
              <div className={clsx("mt-2 flex gap-2", ListIndent())}>
                <i>Example:</i>
                <Code>
                  <span className="text-interstellar">
                    northbeam.io?utm_source=tradedesk
                  </span>
                  &nb_platform=tradedesk…
                </Code>
              </div>
            </>
            <>
              If you are not using existing parameters, please use{" "}
              <Code>?</Code>{" "}
              <div className={clsx("mt-2 flex gap-2", ListIndent())}>
                <i>Example:</i>
                <Code>
                  <span className="text-interstellar">northbeam.io</span>
                  ?nb_platform=tradedesk…
                </Code>
              </div>
            </>
          </OrderedList>
        </div>
      </SetupSection>
      <SetupSection title="Configure sending daily scheduled CSV reports">
        <VideoContent
          title="Learn how to schedule TradeDesk reports"
          platform={PLATFORM}
          videoUrl="https://www.loom.com/embed/7ba47b81005a4041911ab150e6081333?sid=bb58bdd0-3f60-4595-8376-a745cd86c3f0"
        />
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>Create a TSV/CSV report</>
          <div className="inline-table">
            <div className="flex gap-2">
              <span>Your report’s ‘Template Name’ Should be</span>
              <Code>northbeam_scheduled_report</Code>
            </div>
          </div>
          <div className="inline-table">
            <div className="flex flex-col gap-2">
              <span>Add the following fields, metrics and conversions</span>
              <Code>
                <p>Add Fields:</p>
                <ul>
                  <li>Date</li>
                  <li>Ad Group</li>
                  <li>Ad Group ID</li>
                  <li>Campaign</li>
                  <li>Campaign ID</li>
                  <li>Creative</li>
                  <li>Creative ID</li>
                  <li>Advertiser Currency Code</li>
                  <li>Media Type</li>
                </ul>
                <br />
                <p>Add Metrics:</p>
                <ul>
                  <li>Clicks</li>
                  <li>Impressions</li>
                  <li>Advertiser Cost (Adv Currency)</li>
                  <li>Advertiser Cost (USD)</li>
                </ul>
                <br />
                <p>
                  Add Report Column:
                  <br />
                  01
                </p>
                <p>Add Conversions:</p>
                <ul>
                  <li>Click Conversion</li>
                  <li>Click Conversion Revenue</li>
                  <li>View Through Conversion</li>
                  <li>View Through Conversion Revenue</li>
                </ul>
              </Code>
            </div>
          </div>
          <>
            Configure additional fields
            <OrderedList indent={1} className="mt-2">
              {[
                <>
                  <span>Filter by Partner or Advertiser</span>
                  <Code>select your brand name</Code>
                </>,
                <>
                  <span>Date Range</span>
                  <Code>Last 30 days</Code>
                </>,
                <>
                  <span>Date Format</span>
                  <Code>Sortable</Code>
                </>,
                <>
                  <span>Numeric Format</span>
                  <Code>US</Code>
                </>,
                <>
                  <span>Time Zone</span>
                  <Code>UTC</Code>
                </>,
                <>
                  <span>File Format</span>
                  <Code>CSV</Code>
                </>,
                <>
                  <span>Include Headers</span>
                </>,
                <>
                  <span>Email this report to:</span>
                  <Code>
                    {user.displayUserId}zzznbzzztradedesk@robot.northbeam.io
                  </Code>
                </>,
                <>
                  <span>Recurring scheduling</span>
                  <span>{">"}</span>
                  <span>Schedule for recurring</span>
                  <span>{">"}</span>
                  <Code>Daily</Code>
                </>,
              ].map((content, index) => {
                return (
                  <div key={index} className="inline-table">
                    <div className="inline-flex gap-2 justify-center items-center">
                      {content}
                    </div>
                  </div>
                );
              })}
            </OrderedList>
          </>
        </OrderedList>
      </SetupSection>
      <SetupSection title="Connect your TradeDesk Account">
        {connectionContent}
      </SetupSection>
      <URLVerification platform={PLATFORM} />
    </>
  );
};

const TradeDeskManualSetup = () => (
  <>
    <SetupSection title="Locate your Northbeam Client ID, Website URL, Collector Subdomain, and email address for reports">
      <OrderedList variant="upper-alpha" className="space-y-6 font-semibold">
        <>
          Locate your Northbeam Client ID
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>
              Find your Northbeam Client ID in the your browser’s URL slug
              <div className="flex gap-2 items-center mt-2">
                <i>Example:</i>
                <Code>
                  <span className="text-interstellar">
                    dashboard.northbeam.io/
                  </span>
                  87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7
                </Code>
              </div>
            </>
          </OrderedList>
        </>
        <>
          Locate your Website URL
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>Navigate to ’Settings’</>
            <>
              Find it in ’Profile’, under ’Domain Name’{" "}
              <div className="flex gap-2 items-center mt-2">
                <i>Example:</i>
                <Code>www.apple.com</Code>
              </div>
            </>
          </OrderedList>
        </>
        <>
          Locate your ‘Collector Subdomain’
          <OrderedList indent={1} className="font-normal">
            <>Log into Northbeam</>
            <>Navigate to ’Settings’</>
            <>Navigate to ‘Setup DNS Entry’</>
            <>
              Find the subdomain listed (e.g. i, i2, i3, or i4.){" "}
              <div className="flex gap-2 items-center mt-2">
                <i>Example:</i>
                <Code>i</Code>
              </div>
            </>
          </OrderedList>
        </>
        <>
          Create the email address to send scheduled reports to
          <OrderedList indent={1} className="font-normal">
            <>
              Create an email using the following naming convention
              <Code className="ml-2">
                <span className="text-interstellar">
                  your-northbeam-client-id
                </span>
                zzznbzzztradedesk@robot.northbeam.io
              </Code>
              <div className="flex gap-2 items-center mt-2">
                <i>Example:</i>
                <span>Client ID =</span>
                <Code>87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7</Code>
                <span>Email =</span>
                <Code>
                  87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7zzznbzzztradedesk@robot.northbeam.io
                </Code>
              </div>
            </>
          </OrderedList>
        </>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Insert click and impression tracking UTMs">
      <p>
        In order for us to fetch impressions, we need a specific Impression
        Tracking URL to be placed. Every customer has a unique URL -- based on
        the information above.
      </p>
      <VideoContent
        platform={PLATFORM}
        title="Learn how to structure TradeDesk UTMs"
        videoUrl="https://www.loom.com/embed/1577c9d4a61e45519b87b21ad7d202f4?sid=5880cbf7-ab25-4a82-b7f4-6b625b391338"
      />
      <div className="space-y-2">
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>Log into Trade desk</>
          <>
            Update ‘Click Through’ URLs with the following UTM
            <Code block className={clsx("mt-2", ListIndent())}>
              {/* eslint-disable max-len */}
              nb_platform=tradedesk&nb_campaignid=%%TTD_CAMPAIGNID%%&nb_adsetid=%%TTD_ADGROUPID%%&nb_creativeid=%%TTD_CREATIVEID%%&nb_devicefamily=%%TTD_DEVICEOSFAMILY%%&nb_devicetype=%%TTD_DEVICETYPE%%&nbt_site=%%TTD_SITE%%&nbt_vendor=%%TTD_SUPPLYVENDOR_INT%%
            </Code>
          </>
          <>
            An example URL with the Click UTM.
            <div className={clsx("mt-2 space-y-2", ListIndent())}>
              <div>
                <i>Example, do not copy: </i>
              </div>
              <Code block>
                https://northbeam.io?nb_platform=tradedesk&nb_campaignid=%%TTD_CAMPAIGNID%%&nb_adsetid=%%TTD_ADGROUPID%%&nb_creativeid=%%TTD_CREATIVEID%%&nb_devicefamily=%%TTD_DEVICEOSFAMILY%%&nb_devicetype=%%TTD_DEVICETYPE%%&nbt_site=%%TTD_SITE%%&nbt_vendor=%%TTD_SUPPLYVENDOR_INT%%
              </Code>
            </div>
          </>
        </OrderedList>
      </div>
      <div>
        <div className="flex gap-2">
          <b>Please note:</b>
          <span>It’s important to ensure your UTM begins with</span>
          <Code>&</Code>
          <span>or</span>
          <Code>?</Code>
        </div>
        <OrderedList variant="disc">
          <>
            If you are using existing parameters, please use <Code>&</Code>{" "}
            <div className={clsx("mt-2 flex gap-2", ListIndent())}>
              <i>Example:</i>
              <Code>
                <span className="text-interstellar">
                  northbeam.io?utm_source=tradedesk
                </span>
                &nb_platform=tradedesk…
              </Code>
            </div>
          </>
          <>
            If you are not using existing parameters, please use <Code>?</Code>{" "}
            <div className={clsx("mt-2 flex gap-2", ListIndent())}>
              <i>Example:</i>
              <Code>
                <span className="text-interstellar">northbeam.io</span>
                ?nb_platform=tradedesk…
              </Code>
            </div>
          </>
        </OrderedList>
      </div>
    </SetupSection>
    <SetupSection title="Set up impression UTMs">
      <SubSubSectionHeader>
        Make sure you’ve followed these steps:
      </SubSubSectionHeader>
      <OrderedList variant="upper-alpha">
        <>Log into Trade desk</>
        <>
          Update ‘Pixel Impression Tracking’ URLs with the following UTM
          <div className={clsx("mt-2", ListIndent())}>
            <Code block>{URL_UTMS}</Code>
          </div>
        </>
        <>
          An example URL with the Click UTM.
          <div className={clsx("mt-2 flex flex-col gap-2", ListIndent())}>
            <i>Example, do not copy: </i>
            <Code>
              https://i.northbeam.io/i?aid=925160f8-ecc3-4457-8c3e-b75fe5dbf0ac&e=se&se_ca=northbeam&se_ac=impression&se_la=tradedesk&se_pr=%%TTD_CAMPAIGNID%%%7C%3A%7C%%TTD_ADGROUPID%%%7C%3A%7C%%TTD_CREATIVEID%%%7C%3A%7C%%TTD_DEVICEOSFAMILY%%%7C%3A%7C%%TTD_DEVICETYPE%%%7C%3A%7C%%TTD_SITE%%%7C%3A%7C%%TTD_SUPPLYVENDOR_INT%%
            </Code>
          </div>
        </>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Configure sending daily scheduled CSV reports">
      <VideoContent
        title="Learn how to schedule TradeDesk reports"
        platform={PLATFORM}
        videoUrl="https://www.loom.com/embed/7ba47b81005a4041911ab150e6081333?sid=bb58bdd0-3f60-4595-8376-a745cd86c3f0"
      />
      <SubSubSectionHeader>
        Make sure you’ve followed these steps:
      </SubSubSectionHeader>
      <OrderedList variant="upper-alpha">
        <>Create a TSV/CSV report</>
        <div className="inline-table">
          <div className="flex gap-2">
            <span>Your report’s ‘Template Name’ Should be</span>
            <Code>northbeam_scheduled_report</Code>
          </div>
        </div>
        <div className="inline-table">
          <div className="flex flex-col gap-2">
            <span>Add the following fields, metrics and conversions</span>
            <Code>
              <p>Add Fields:</p>
              <ul>
                <li>Date</li>
                <li>Ad Group</li>
                <li>Ad Group ID</li>
                <li>Campaign</li>
                <li>Campaign ID</li>
                <li>Creative</li>
                <li>Creative ID</li>
                <li>Advertiser Currency Code</li>
                <li>Media Type</li>
              </ul>
              <br />
              <p>Add Metrics:</p>
              <ul>
                <li>Clicks</li>
                <li>Impressions</li>
                <li>Advertiser Cost (Adv Currency)</li>
                <li>Advertiser Cost (USD)</li>
              </ul>
              <br />
              <p>
                Add Report Column:
                <br />
                01
              </p>
              <p>Add Conversions:</p>
              <ul>
                <li>Click Conversion</li>
                <li>Click Conversion Revenue</li>
                <li>View Through Conversion</li>
                <li>View Through Conversion Revenue</li>
              </ul>
            </Code>
          </div>
        </div>
        <>
          Configure additional fields
          <OrderedList indent={1} className="mt-2">
            {[
              <>
                <span>Filter by Partner or Advertiser</span>
                <Code>select your brand name</Code>
              </>,
              <>
                <span>Date Range</span>
                <Code>Last 30 days</Code>
              </>,
              <>
                <span>Date Format</span>
                <Code>Sortable</Code>
              </>,
              <>
                <span>Numeric Format</span>
                <Code>US</Code>
              </>,
              <>
                <span>Time Zone</span>
                <Code>UTC</Code>
              </>,
              <>
                <span>File Format</span>
                <Code>CSV</Code>
              </>,
              <>
                <span>Include Headers</span>
              </>,
              {
                content: (
                  <>
                    <span>Email this report to:</span>
                    <Code>
                      [your_client_id]
                      <span className="text-interstellar">
                        zzznbzzztradedesk@robot.northbeam.io
                      </span>
                    </Code>
                  </>
                ),
                additionalContent: (
                  <div className="flex flex-col gap-2 mt-2 ml-2">
                    <div className="flex gap-2 items-center">
                      <span>Find Client ID in your NB Dashboard URL</span>
                      <Code>
                        <span className="text-interstellar">
                          dashboard.northbeam.io/
                        </span>
                        87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7
                      </Code>
                    </div>
                    <div className="flex gap-2 items-center">
                      <i>Example</i>
                      <Code>
                        87aaeb29-3b46-4ddf-b824-11e6e3b9d3a7zzznbzzztradedesk@robot.northbeam.io
                      </Code>
                    </div>
                  </div>
                ),
              },
              <>
                <span>Recurring scheduling</span>
                <span>{">"}</span>
                <span>Schedule for recurring</span>
                <span>{">"}</span>
                <Code>Daily</Code>
              </>,
            ].map((content, index) => {
              return Object.hasOwn(content, "content") ? (
                <div key={index} className="inline-table">
                  <div className="inline-flex gap-2 justify-center items-center">
                    {(content as { content: JSX.Element }).content}
                  </div>
                  {
                    (content as { additionalContent: JSX.Element })
                      .additionalContent
                  }
                </div>
              ) : (
                <div key={index} className="inline-table">
                  <div className="inline-flex gap-2 justify-center items-center">
                    {content}
                  </div>
                </div>
              );
            })}
          </OrderedList>
        </>
      </OrderedList>
    </SetupSection>
    <SetupSection title="Send us a ticket telling us you’ve completed setting up UTMs and Reports">
      <SubmitTicketButton />
    </SetupSection>
    <URLVerification platform={PLATFORM} />
  </>
);
